'use strict';

import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';

const FONT_KEY = 'kire-ssh-font-size';
const FONT_MIN = 11;
const FONT_MAX = 22;

const THEME = {
  background: '#090b0e',
  foreground: '#c5cdd6',
  cursor: '#00bff6',
  cursorAccent: '#090b0e',
  selectionBackground: 'rgba(0, 191, 246, 0.28)',
  selectionForeground: '#0b0d10',
  black: '#0b0d10',
  red: '#e85d5d',
  green: '#7dff6b',
  yellow: '#e6b325',
  blue: '#6b8cff',
  magenta: '#c08bff',
  cyan: '#6be0ff',
  white: '#c5cdd6',
  brightBlack: '#8b939e',
  brightRed: '#ff7a7a',
  brightGreen: '#9dff90',
  brightYellow: '#f0c94a',
  brightBlue: '#8ea4ff',
  brightMagenta: '#d4a8ff',
  brightCyan: '#8eebff',
  brightWhite: '#eef2f6',
};

function $(sel, root) {
  return (root || document).querySelector(sel);
}

function wsUrl(path) {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return proto + '//' + location.host + (path || '/ws');
}

function esc(s) {
  return String(s).replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[ch]));
}

function loadPack() {
  const el = document.getElementById('kire-terminal');
  if (!el) return {};
  try {
    const pack = JSON.parse(el.textContent);
    return pack && typeof pack === 'object' ? pack : {};
  } catch (e) {
    return {};
  }
}

const PACK = loadPack();

function t(key, fallback) {
  const v = PACK[key];
  if (typeof v === 'string' && v !== '') return v;
  return fallback;
}

function trMsg(s) {
  if (s == null || s === '') return s;
  const map = PACK.msg;
  if (map && typeof map[s] === 'string' && map[s] !== '') return map[s];
  return s;
}

function fill(template, vars) {
  return String(template).replace(/\{([a-z0-9_]+)\}/g, (_, name) => {
    const value = vars && Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : '';
    return esc(value);
  });
}

function stateLabel(next) {
  if (next === 'connecting') return t('state_connecting', 'connecting');
  if (next === 'connected') return t('state_connected', 'connected');
  if (next === 'disconnected') return t('state_disconnected', 'disconnected');
  if (next === 'error') return t('state_error', 'error');
  return next;
}

function copyText(text, el) {
  function done() {
    if (!el) return;
    el.setAttribute('data-copied', '1');
    const hint = el.querySelector('.hint');
    const prev = hint ? hint.textContent : '';
    if (hint) hint.textContent = t('copied', 'copied');
    setTimeout(() => {
      el.removeAttribute('data-copied');
      if (hint) hint.textContent = prev;
    }, 1200);
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => {
      fallbackCopy(text);
      done();
    });
  } else {
    fallbackCopy(text);
    done();
  }
}

function fallbackCopy(text) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'absolute';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } catch (e) { /* ignore */ }
  document.body.removeChild(ta);
}

function loadFontSize() {
  const n = parseInt(localStorage.getItem(FONT_KEY) || '14', 10);
  if (!Number.isFinite(n)) return 14;
  return Math.min(FONT_MAX, Math.max(FONT_MIN, n));
}

function renderApp(root, cfg) {
  const host = cfg.host;
  const port = cfg.port;
  const target = host + ':' + port;

  root.innerHTML =
    '<div id="ssh-connect"><div class="ssh-hero-grid">' +
      '<div>' +
        '<p class="cmd"><span class="hash">$</span>./terminal</p>' +
        '<h1>' + t('h1', 'Your <s>browser</s> shell.') + '</h1>' +
        '<p class="lede">' + esc(t('lede', 'Username and a password or key. We open a real SSH session. This is a luxury you\u2019ll only find at KIRE. OpenSSH still works.')) + '</p>' +
        '<div class="ssh-notes">' +
          '<h2>' + esc(t('notes', './notes')) + '</h2>' +
          '<p>' + t('note1', 'Browsers cannot speak SSH. So, our geeks engineered a small WebSocket gateway on this site that proxies to the same box you already <span class="mono">ssh</span> into. screen and tmux are fine. So is Ctrl-C.') + '</p>' +
          '<p>' + esc(t('note2', 'Pasting a private key into a web page is a trade. We keep it in memory for this session only. Prefer a local client if that sentence bothers you — it should, a little. But we \u2764\uFE0F simplicity and security.')) + '</p>' +
        '</div>' +
      '</div>' +
      '<div class="term" id="connect-term">' +
        '<div class="term-bar">' +
          '<span class="term-dots"></span>' +
          '<span>kire@net: ~</span>' +
        '</div>' +
        '<div class="term-body">' +
          '<div class="term-line"><span class="prompt">kire@net:~$</span> <span class="cmd-in">ssh ' + esc(host) + '</span></div>' +
          '<div class="target-line">' + fill(t('target', 'connected target <span class="val">{target}</span> — not an input'), { target: target }) + '</div>' +
          '<form class="ssh-form" id="ssh-form" autocomplete="on">' +
            '<div id="ssh-flash" hidden></div>' +
            '<label class="field"><span>' + esc(t('user', 'username')) + '</span>' +
              '<input id="ssh-user" name="username" type="text" required maxlength="32" autocapitalize="off" spellcheck="false" autocomplete="username">' +
            '</label>' +
            '<div class="field">' +
              '<span>' + esc(t('auth', 'auth method')) + '</span>' +
              '<div class="auth-switch">' +
                '<label><input type="radio" name="method" value="password" checked> ' + esc(t('password', 'password')) + '</label>' +
                '<label><input type="radio" name="method" value="key"> ' + esc(t('key', 'private key')) + '</label>' +
              '</div>' +
            '</div>' +
            '<label class="field" id="pw-field"><span>' + esc(t('password', 'password')) + '</span>' +
              '<input id="ssh-pass" name="password" type="password" autocomplete="current-password">' +
            '</label>' +
            '<div id="key-fields" hidden>' +
              '<div class="field">' +
                '<span>' + esc(t('key', 'private key')) + '</span>' +
                '<span class="key-stack">' +
                  '<textarea id="ssh-key" name="privateKey" spellcheck="false" autocomplete="off" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"></textarea>' +
                  '<label class="file-pick">' +
                    '<input id="ssh-keyfile" type="file" accept=".pem,.key,.txt,text/plain">' +
                    '<span>' + esc(t('choose', 'choose file')) + '</span>' +
                  '</label>' +
                '</span>' +
              '</div>' +
              '<label class="field"><span>' + esc(t('passphrase', 'key passphrase')) + ' <span class="muted">' + esc(t('optional', '(optional)')) + '</span></span>' +
                '<input id="ssh-keypass" type="password" autocomplete="off">' +
              '</label>' +
              '<p class="key-warn">' + esc(t('key_warn', 'You are about to paste a private key in the browser. Convenience, not a replacement for OpenSSH. We do not write it to disk.')) + '</p>' +
            '</div>' +
            '<div class="btn-row">' +
              '<button class="btn" type="submit" id="ssh-connect-btn">' + esc(t('connect', 'connect')) + '</button>' +
              '<button class="copy" type="button" data-copy="ssh user@kire.net" id="ssh-copy">' +
                '<code>ssh user@kire.net</code><span class="hint">' + esc(t('copy', 'copy')) + '</span>' +
              '</button>' +
            '</div>' +
          '</form>' +
        '</div>' +
      '</div>' +
    '</div></div>';
}

function renderSession(cfg) {
  let el = document.getElementById('ssh-session');
  if (el) return el;
  el = document.createElement('div');
  el.id = 'ssh-session';
  el.className = 'ssh-session';
  el.innerHTML =
    '<div class="ssh-toolbar">' +
      '<span class="who" id="ssh-who"></span>' +
      '<span class="sep">•</span>' +
      '<span>' + cfg.host + ':' + cfg.port + '</span>' +
      '<span class="sep">•</span>' +
      '<span class="state" id="ssh-state">' + esc(t('state_connecting', 'connecting')) + '</span>' +
      '<span class="spacer"></span>' +
      '<button class="btn ghost" type="button" id="ssh-font-minus" title="' + esc(t('smaller', 'smaller')) + '">−</button>' +
      '<button class="btn ghost" type="button" id="ssh-font-plus" title="' + esc(t('larger', 'larger')) + '">+</button>' +
      '<button class="btn ghost" type="button" id="ssh-paste">' + esc(t('paste', 'paste')) + '</button>' +
      '<button class="btn ghost" type="button" id="ssh-reconnect" hidden>' + esc(t('reconnect', 'reconnect')) + '</button>' +
      '<button class="btn ghost" type="button" id="ssh-new" hidden>' + esc(t('new', 'new session')) + '</button>' +
      '<button class="btn" type="button" id="ssh-disconnect">' + esc(t('disconnect', 'disconnect')) + '</button>' +
    '</div>' +
    '<div class="ssh-term" id="ssh-term">' +
      '<div class="ssh-banner" id="ssh-banner"></div>' +
    '</div>';
  document.body.appendChild(el);
  return el;
}

function init() {
  const mount = document.getElementById('ssh-app');
  if (!mount) return;

  const cfg = {
    host: mount.getAttribute('data-target-host') || 'kire.net',
    port: mount.getAttribute('data-target-port') || '22',
    ws: mount.getAttribute('data-ws-url') || wsUrl(mount.getAttribute('data-ws-path') || '/ws'),
  };

  renderApp(mount, cfg);
  const sessionEl = renderSession(cfg);

  const form = $('#ssh-form');
  const flash = $('#ssh-flash');
  const userEl = $('#ssh-user');
  const passEl = $('#ssh-pass');
  const keyEl = $('#ssh-key');
  const keyPassEl = $('#ssh-keypass');
  const keyFileEl = $('#ssh-keyfile');
  const pwField = $('#pw-field');
  const keyFields = $('#key-fields');
  const connectBtn = $('#ssh-connect-btn');
  const stateEl = $('#ssh-state');
  const whoEl = $('#ssh-who');
  const banner = $('#ssh-banner');
  const termHost = $('#ssh-term');
  const reconnectBtn = $('#ssh-reconnect');
  const newBtn = $('#ssh-new');
  const disconnectBtn = $('#ssh-disconnect');

  let method = 'password';
  let creds = null;
  let ws = null;
  let term = null;
  let fit = null;
  let fontSize = loadFontSize();
  let state = 'form';
  let disposed = false;

  function setFlash(msg, isErr) {
    if (!msg) {
      flash.hidden = true;
      flash.textContent = '';
      return;
    }
    flash.hidden = false;
    flash.className = 'flash' + (isErr ? ' err' : '');
    flash.textContent = trMsg(msg);
  }

  function setState(next, message) {
    state = next;
    stateEl.textContent = stateLabel(next);
    stateEl.classList.toggle('is-on', next === 'connected');
    stateEl.classList.toggle('is-err', next === 'error' || next === 'disconnected');
    const inSession = next === 'connecting' || next === 'connected' || next === 'disconnected' || (next === 'error' && document.body.classList.contains('is-session'));
    document.body.classList.toggle('is-session', inSession && next !== 'form');
    reconnectBtn.hidden = !(next === 'disconnected' || next === 'error');
    newBtn.hidden = !(next === 'disconnected' || next === 'error');
    disconnectBtn.hidden = next === 'disconnected' || next === 'error';
    if (message && (next === 'error' || next === 'disconnected')) {
      banner.textContent = trMsg(message);
      banner.classList.add('is-on');
    } else if (next === 'connected' || next === 'connecting') {
      banner.classList.remove('is-on');
    }
  }

  function methodFromForm() {
    const picked = form.querySelector('input[name="method"]:checked');
    return picked ? picked.value : 'password';
  }

  function toggleMethod() {
    method = methodFromForm();
    pwField.hidden = method !== 'password';
    keyFields.hidden = method !== 'key';
    passEl.required = method === 'password';
  }

  form.addEventListener('change', (e) => {
    if (e.target && e.target.name === 'method') toggleMethod();
  });
  toggleMethod();

  keyFileEl.addEventListener('change', () => {
    const f = keyFileEl.files && keyFileEl.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      keyEl.value = String(reader.result || '');
    };
    reader.readAsText(f);
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy]');
    if (!btn) return;
    e.preventDefault();
    copyText(btn.getAttribute('data-copy'), btn);
  });

  const nav = document.querySelector('.nav');
  const toggle = document.querySelector('.nav-toggle');
  if (nav && toggle && !document.querySelector('script[src*="kire.js"]')) {
    toggle.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  function destroyTerm() {
    if (term) {
      try { term.dispose(); } catch (e) { /* ignore */ }
      term = null;
      fit = null;
    }
    const old = termHost.querySelector('.xterm');
    if (old) old.remove();
  }

  function ensureTerm() {
    if (term) return term;
    term = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontFamily: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize,
      lineHeight: 1.2,
      theme: THEME,
      scrollback: 5000,
      convertEol: false,
      allowProposedApi: true,
      macOptionIsMeta: true,
    });
    fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());
    term.open(termHost);
    term.onData((data) => {
      if (ws && ws.readyState === 1 && state === 'connected') {
        ws.send(new TextEncoder().encode(data));
      }
    });
    term.onResize(({ cols, rows }) => {
      if (ws && ws.readyState === 1 && state === 'connected') {
        ws.send(JSON.stringify({ type: 'resize', cols, rows }));
      }
    });
    requestAnimationFrame(() => {
      try { fit.fit(); } catch (e) { /* ignore */ }
      term.focus();
    });
    return term;
  }

  function fitNow() {
    if (!fit || !term) return;
    try { fit.fit(); } catch (e) { /* ignore */ }
  }

  function applyViewport() {
    const session = document.getElementById('ssh-session');
    if (!session || !document.body.classList.contains('is-session')) return;
    const vv = window.visualViewport;
    const header = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--status-h')) || 34)
      + (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-h')) || 58);
    if (vv) {
      const top = Math.max(header, header - (window.scrollY || 0));
      session.style.top = Math.min(header, Math.max(0, header - vv.offsetTop)) + 'px';
      session.style.height = Math.max(120, vv.height - Math.min(header, top)) + 'px';
      session.style.left = vv.offsetLeft + 'px';
      session.style.width = vv.width + 'px';
      session.style.bottom = 'auto';
    } else {
      session.style.top = header + 'px';
      session.style.left = '0';
      session.style.right = '0';
      session.style.bottom = '0';
      session.style.height = '';
      session.style.width = '';
    }
    fitNow();
  }

  window.addEventListener('resize', applyViewport);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', applyViewport);
    window.visualViewport.addEventListener('scroll', applyViewport);
  }

  function closeWs() {
    if (!ws) return;
    try { ws.close(1000); } catch (e) { /* ignore */ }
    ws = null;
  }

  function readCreds() {
    const username = userEl.value.trim();
    const m = methodFromForm();
    if (!username) throw new Error('Username required');
    if (m === 'key') {
      const privateKey = keyEl.value.trim();
      if (!privateKey) throw new Error('Private key required');
      return {
        username,
        method: 'key',
        privateKey,
        passphrase: keyPassEl.value || undefined,
      };
    }
    const password = passEl.value;
    if (!password) throw new Error('Password required');
    return { username, method: 'password', password };
  }

  function clearSecrets() {
    passEl.value = '';
    keyEl.value = '';
    keyPassEl.value = '';
    keyFileEl.value = '';
    if (creds) {
      creds.password = undefined;
      creds.privateKey = undefined;
      creds.passphrase = undefined;
    }
    creds = creds ? { username: creds.username, method: creds.method } : null;
  }

  function backToForm(opts) {
    opts = opts || {};
    closeWs();
    destroyTerm();
    document.body.classList.remove('is-session');
    setState('form');
    banner.classList.remove('is-on');
    connectBtn.disabled = false;
    if (opts.clearSecrets !== false) clearSecrets();
    if (creds && creds.username) userEl.value = creds.username;
    if (opts.message) setFlash(opts.message, true);
    userEl.focus();
  }

  function connect(existing) {
    let payload;
    try {
      payload = existing || readCreds();
    } catch (err) {
      setFlash(err.message, true);
      return;
    }
    creds = payload;
    whoEl.textContent = payload.username + '@' + cfg.host;
    setFlash('');
    connectBtn.disabled = true;
    document.body.classList.add('is-session');
    setState('connecting');
    destroyTerm();
    ensureTerm();
    applyViewport();

    closeWs();
    const socket = new WebSocket(cfg.ws);
    socket.binaryType = 'arraybuffer';
    ws = socket;
    disposed = false;

    socket.onopen = () => {
      const cols = term ? term.cols : 80;
      const rows = term ? term.rows : 24;
      socket.send(JSON.stringify({
        type: 'auth',
        username: payload.username,
        method: payload.method,
        password: payload.method === 'password' ? payload.password : undefined,
        privateKey: payload.method === 'key' ? payload.privateKey : undefined,
        passphrase: payload.method === 'key' ? payload.passphrase : undefined,
        cols,
        rows,
      }));
    };

    socket.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        let msg;
        try { msg = JSON.parse(ev.data); } catch (e) { return; }
        if (msg.type === 'status') {
          if (msg.state === 'connected') {
            setState('connected');
            if (term) term.focus();
            fitNow();
          } else if (msg.state === 'connecting') {
            setState('connecting');
          } else if (msg.state === 'disconnected') {
            setState('disconnected', 'Disconnected.');
          }
        } else if (msg.type === 'error') {
          const text = msg.message || 'Connection failed';
          if (state === 'connecting' || !document.body.classList.contains('is-session')) {
            backToForm({ message: text, clearSecrets: true });
          } else {
            setState('error', text);
          }
        }
        return;
      }
      if (term) term.write(new Uint8Array(ev.data));
    };

    socket.onerror = () => {
      if (disposed) return;
      if (state === 'connecting') {
        backToForm({ message: 'Connection failed', clearSecrets: true });
      } else {
        setState('error', 'Connection failed');
      }
    };

    socket.onclose = (ev) => {
      if (disposed) return;
      if (state === 'connecting') {
        const reason = ev.reason || 'Connection failed';
        backToForm({ message: reason, clearSecrets: true });
        return;
      }
      if (state === 'connected') {
        setState('disconnected', 'Disconnected.');
      }
    };
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    connect();
  });

  disconnectBtn.addEventListener('click', () => {
    disposed = true;
    backToForm({ clearSecrets: true });
  });

  reconnectBtn.addEventListener('click', () => {
    if (!creds || (!creds.password && !creds.privateKey)) {
      backToForm({ message: 'Re-enter your password or key.' });
      return;
    }
    connect(creds);
  });

  newBtn.addEventListener('click', () => {
    disposed = true;
    const name = (creds && creds.username) || userEl.value;
    backToForm({ clearSecrets: true });
    userEl.value = name;
  });

  $('#ssh-font-minus').addEventListener('click', () => {
    fontSize = Math.max(FONT_MIN, fontSize - 1);
    localStorage.setItem(FONT_KEY, String(fontSize));
    if (term) term.options.fontSize = fontSize;
    fitNow();
  });
  $('#ssh-font-plus').addEventListener('click', () => {
    fontSize = Math.min(FONT_MAX, fontSize + 1);
    localStorage.setItem(FONT_KEY, String(fontSize));
    if (term) term.options.fontSize = fontSize;
    fitNow();
  });

  $('#ssh-paste').addEventListener('click', async () => {
    if (!term) return;
    try {
      const text = await navigator.clipboard.readText();
      if (text && ws && ws.readyState === 1) {
        ws.send(new TextEncoder().encode(text));
      }
    } catch (e) {
      term.focus();
    }
  });

  termHost.addEventListener('click', () => {
    if (term) term.focus();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
